// src/services/auth.service.ts
import axios from 'axios';
import { User } from '../types/user';
import { rememberEspaceFromUser } from '../utils/postLogout';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:1920/api';

// Créer une instance axios configurée
const api = axios.create({
  baseURL: API_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Intercepteur requêtes
api.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    console.error('❌ Erreur configuration requête:', error);
    return Promise.reject(error);
  }
);

// Intercepteur réponses
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error('❌ Erreur API détaillée:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: error.config?.url,
      method: error.config?.method,
      // NE PAS logger error.config.data : contient le mot de passe en clair
      // pour /auth/login (fuite de credentials dans la console).
      responseData: error.response?.data,
    });
    return Promise.reject(error);
  }
);

export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  user: {
     id: string;
  email: string;
  nom: string;
  prenom: string;
  role: string;
  entiteId: string;
  entitename?: string;
  username: string;
  agentId?: string;
  statut: string;
  isActive: boolean;
  lastLogin?: string;
  createdAt: string;
  updatedAt: string;
   permissions?: string[]; // Ajoutez cette ligne
  };
}



class AuthService {
  private getAuthHeaders() {
    const token = this.getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  // Stocker le token
  setToken(token: string): void {
    localStorage.setItem('access_token', token);
  }

  // Récupérer le token
  getToken(): string | null {
    return localStorage.getItem('access_token');
  }

  // Stocker le refresh token
  setRefreshToken(token: string): void {
    localStorage.setItem('refresh_token', token);
  }

  // Récupérer le refresh token
  getRefreshToken(): string | null {
    return localStorage.getItem('refresh_token');
  }

  // Stocker les infos utilisateur
  setUser(user: User): void {
    localStorage.setItem('user', JSON.stringify(user));
    // Mémorise l'espace (adhérent/agent) pour la redirection de déconnexion (build CGE-GUI).
    rememberEspaceFromUser(user);
  }

  // Récupérer les infos utilisateur
  getUser(): User | null {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  // Connexion - UNIQUEMENT API RÉELLE
  async login(email: string, password: string, rememberMe: boolean = false): Promise<LoginResponse> {
    try {
      // Vérification basique des données
      if (!email || !password) {
        throw new Error('Email et mot de passe sont requis');
      }

      const response = await api.post('/auth/login', {
        identifier: email.trim(),
        password: password.trim(),
      });

      const { access_token, refresh_token, user } = response.data;

      if (!access_token || !user) {
        throw new Error('Réponse invalide du serveur');
      }

      // Stocker les tokens
      this.setToken(access_token);
      this.setRefreshToken(refresh_token);
      this.setUser(user);

      // Si "Se souvenir de moi" est coché, stocker les infos de connexion
      if (rememberMe) {
        localStorage.setItem('remembered_email', email);
      } else {
        localStorage.removeItem('remembered_email');
      }

      return response.data;

    } catch (error: any) {
      console.error('💥 Erreur complète de connexion RÉELLE:', {
        name: error.name,
        message: error.message,
        code: error.code,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        stack: error.stack
      });

      // Gestion spécifique des erreurs
      if (error.response?.status === 401) {
        throw new Error('Email ou mot de passe incorrect');
      } else if (error.response?.status === 403) {
        throw new Error('Votre compte a été désactivé');
      } else if (error.response?.status === 500) {
        // Essayer de récupérer le message d'erreur du serveur
        const serverMessage = error.response?.data?.message || 
                            error.response?.data?.error ||
                            'Erreur interne du serveur. Veuillez contacter l\'administrateur.';
        throw new Error(`Erreur serveur (500): ${serverMessage}`);
      } else if (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error')) {
        throw new Error('Impossible de se connecter au serveur. Vérifiez votre connexion internet et que le serveur est démarré.');
      } else if (error.code === 'ECONNABORTED') {
        throw new Error('La requête a expiré. Le serveur met trop de temps à répondre.');
      } else if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else {
        throw new Error('Erreur de connexion au serveur: ' + (error.message || 'Erreur inconnue'));
      }
    }
  }

  // SUPPRIMER LA MÉTHODE loginWithFallback et mockLogin

  /**
   * Connexion via tokens déjà émis par le backend (SSO SAML SP-initiated).
   * Le backend redirige le navigateur vers /sso/callback?token=…&refresh=…
   * après une assertion SAML valide. On persiste les tokens EXACTEMENT comme
   * le login classique, puis on hydrate l'utilisateur via /auth/profile.
   */
  async loginWithTokens(accessToken: string, refreshToken?: string): Promise<User> {
    if (!accessToken) {
      throw new Error('Token SSO manquant');
    }
    this.setToken(accessToken);
    if (refreshToken) this.setRefreshToken(refreshToken);
    // Hydrate le profil (et synchronise le cache localStorage user via getProfile)
    const userData = await this.getProfile();
    return userData;
  }

  // Déconnexion
  async logout(): Promise<void> {
    const token = this.getToken();
    
    try {
      if (token) {
        await api.post('/auth/logout', {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    } finally {
      // Nettoyer le localStorage
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      localStorage.removeItem('remembered_email');
    }
  }

  // Rafraîchir le token
  async refreshToken(): Promise<{ access_token: string; refresh_token: string }> {
    const refreshToken = this.getRefreshToken();
    
    if (!refreshToken) {
      throw new Error('Aucun token de rafraîchissement disponible');
    }

    try {
      const response = await api.post('/auth/refresh', {
        refresh_token: refreshToken,
      });

      const { access_token, refresh_token } = response.data;

      // Mettre à jour les tokens
      this.setToken(access_token);
      this.setRefreshToken(refresh_token);

      return response.data;
    } catch (error: any) {
      console.error('Erreur de rafraîchissement du token:', error);
      
      // En cas d'erreur, déconnecter l'utilisateur
      this.logout();
      throw new Error('Session expirée, veuillez vous reconnecter');
    }
  }

  // Vérifier si l'utilisateur est connecté
  isAuthenticated(): boolean {
    const token = this.getToken();
    const user = this.getUser();
    
    if (!token || !user) {
      return false;
    }

    // Vérifier l'expiration du token (basique)
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const isExpired = payload.exp * 1000 < Date.now();
      return !isExpired;
    } catch {
      return false;
    }
  }

  // Récupérer l'email mémorisé
  getRememberedEmail(): string | null {
    return localStorage.getItem('remembered_email');
  }

  // Changer le mot de passe
  async changePassword(currentPassword: string, newPassword: string, confirmPassword?: string): Promise<void> {
    try {
      await api.patch('/auth/change-password', {
        currentPassword,
        newPassword,
        confirmPassword: confirmPassword ?? newPassword,
      }, {
        headers: this.getAuthHeaders(),
      });
    } catch (error: any) {
      const msg = error.response?.data?.message;
      if (Array.isArray(msg)) {
        throw new Error(msg.join(', '));
      }
      if (msg) {
        throw new Error(msg);
      }
      throw new Error('Erreur lors du changement de mot de passe');
    }
  }

  // Demande de réinitialisation de mot de passe
  async forgotPassword(email: string): Promise<void> {
    try {
      await api.post('/auth/forgot-password', { email });
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Erreur lors de la demande de réinitialisation');
    }
  }

  // Réinitialiser le mot de passe
  async resetPassword(token: string, newPassword: string): Promise<void> {
    try {
      await api.post('/auth/reset-password', {
        token,
        newPassword,
      });
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Erreur lors de la réinitialisation du mot de passe');
    }
  }

  // Récupérer le profil utilisateur
  async getProfile1(): Promise<User> {
    try {
      const response = await api.get('/auth/profile', {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 401) {
        this.logout();
        throw new Error('Session expirée');
      }
      throw new Error('Erreur lors de la récupération du profil');
    }
  }

  async getProfile(): Promise<User> {
  try {
    const response = await axios.get(`${API_URL}/auth/profile`, {
      headers: this.getAuthHeaders(),
    });
    // Synchroniser localStorage avec le profil frais (scopeEntiteId, droits, etc.)
    // Important pour les users avec affectation boutique/dépôt :
    // scopeEntiteId calculé par le backend doit être disponible dès le prochain
    // accès (y compris sur chemin public où on lit le cache localStorage).
    this.setUser(response.data);
    return response.data;
  } catch (error: any) {
    console.error('❌ Erreur getProfile:', error);
    if (error.response?.status === 401) {
      this.logout();
      throw new Error('Session expirée');
    }
    // La réponse du serveur est conservée : sans elle, l'appelant sait que le
    // serveur était injoignable (réseau coupé) et non qu'il a refusé.
    const e: any = new Error('Erreur lors de la récupération du profil');
    e.response = error.response;
    throw e;
  }
}

  // Mettre à jour le profil
  async updateProfile(profileData: Partial<User>): Promise<User> {
    try {
      const response = await api.patch('/auth/profile', profileData, {
        headers: this.getAuthHeaders(),
      });
      
      // Mettre à jour l'utilisateur dans le localStorage
      const updatedUser = response.data;
      this.setUser(updatedUser);
      
      return updatedUser;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Erreur lors de la mise à jour du profil');
    }
  }

  // Vérifier la validité du token
  async verifyToken(): Promise<boolean> {
    try {
      await api.get('/auth/verify', {
        headers: this.getAuthHeaders(),
      });
      return true;
    } catch {
      return false;
    }
  }
}

export const authService = new AuthService();